import { ColorTypes, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormControlProperties, FormValues } from '@omegagrid/form';
import rp from "@reporting/rp-app-host";
import { User, AccountingGroup, Accounting } from "@reporting/rp-types/core";
import { PASSWORD_POLICIES, PasswordPolicy } from "@reporting/rp-types/accounting";
import { CallerContext, TfaMethods } from "../types";

export class UserFormAdapter extends FormAdapter {

	private pwdPolicy: PasswordPolicy;

	constructor(
		private group: AccountingGroup = null,
		private user: User = null,
		private caller: CallerContext = 'user',
		private enableDelete: boolean = false,
		private options?: FormContainerOptions
	) {
		super();
		this.pwdPolicy = PASSWORD_POLICIES[group?.password_complexity || 1];
	}

	getColumns() {
		return [
			{width: 180},
			{width: null},
		];
	}

	async getControls() {
		let r = 0;
		const rows: FormControlProperties[] = [];
		if (this.caller === 'super') {
			if (!this.user) {
				const accountings = await rp.ms('accounting').get<Accounting[]>(`accountings/${this.group.id_group}`,{
					fields: ['accounting', 'company'],
				});
				rows.push(
					{row: r, col: 0, type: FormControlType.Label, value: msg('Company'), for: 'accounting'},
					{row: r++, col: 1, type: FormControlType.List, key: 'accounting', value: accountings[0].accounting, required: true, content: {
						sourceData: accountings.map(a => ({ k: a.accounting, v: `${a.accounting} ${a.company}` })),
						options: { selectMode: 'single', autoClose: true }
					}},
				);
			}
			if (this.user && rp.userProvider.isSA()) {
				const groups = await rp.ms('accounting').get<AccountingGroup[]>(`userGroups/${this.user.user_name}`, { fields: ['id_group', 'user_domain'] });
				rows.push(
					{row: r, col: 0, type: FormControlType.Label, value: msg('Domain'), for: 'domain'},
					{row: r++, col: 1, type: FormControlType.List, key: 'domain', value: this.user.id_accounting_group || null, required: true, content: {
						sourceData: [{k: null, v: '-'}, ...groups.map(d => ({ k: d.id_group, v: d.user_domain }))],
						options: { selectMode: 'single', autoClose: true }
					}},
				);
			}
		}
		rows.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('User'), for: 'login_name'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'login_name', value: this.user?.login_name || null, required: true, readOnly: this.user ? true : false},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Email'), for: 'email'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'email', value: this.user?.email || null, pattern: /^$|^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Phone'), for: 'phone'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'phone', value: this.user?.phone || null, pattern: /^$|^\+?[0-9\s-]+$/},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Two-Factor Authentication'), for: 'tfa_method'},
			{row: r++, col: 1, type: FormControlType.List, key: 'tfa_method', value: this.user?.tfa_method || 0, required: true, content: {
				sourceData: [
					{ k: 0, v: msg('None') },
					{ k: TfaMethods.TFA_EMAIL, v: 'Email' },
					// { k: TfaMethods.TFA_SMS, v: 'SMS' },
					{ k: TfaMethods.TFA_APP, v: msg('Mobile app') }
				],
				options: { selectMode: 'single', autoClose: true }
			}},
		);
		if (!this.user) {
			rows.push(
				{row: r, col: 0, type: FormControlType.Label, value: msg('Password'), for: 'password'},
				{row: r++, col: 1, type: FormControlType.PasswordInput, key: 'password', required: true, pattern: this.pwdPolicy.pattern, placeholder: msg(this.pwdPolicy.text)},
				{row: r, col: 0, type: FormControlType.Label, value: msg('Confirm Password'), for: '_confirmPassword'},
				{row: r++, col: 1, type: FormControlType.PasswordInput, key: '_confirmPassword', required: true}
			);
		}
		return rows;
	}

	getOptions(): FormContainerOptions {
		return this.options || {
			caption: msg('User details'),
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: [
				...this.enableDelete ? [{key: 'delete', text: msg('Delete user'), color: ColorTypes.Red}] : [],
				"save"
			]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		if (formValues['password'] !== formValues['_confirmPassword']) {
			rp.host.bucket.showAlert({content: msg('Passwords do not match.'), type: 'danger'});
			return false;
		}

		const user: User = {
			user_name: this.user?.user_name || null,
			login_name: this.user?.login_name || formValues['login_name'] as string,
			id_accounting_group: formValues['domain'] as number || null,
			email: formValues['email'] as string || null,
			phone: formValues['phone'] as string || null,
			password: formValues['password'] as string || null,
			tfa_method: formValues['tfa_method'] as number || null,
		};
		await rp.ms('users').post('users', Object.assign({
			g: this.group?.id_group || null,
			a: formValues['accounting'] as number || null
		}, user));

		if (this.caller == 'user' && user.tfa_method === TfaMethods.TFA_APP && this.user.tfa_method !== TfaMethods.TFA_APP) {
			this.showTfaAppDialog();
		}

		return true;
	}

	showTfaAppDialog(): void {
		const div = dom.createElement<HTMLDivElement>('div');
		div.style.margin = '15px';
		div.innerHTML = `
			${msg('Set up two-factor authentication in the mobile app. Reopen the settings to display the QR code and secret key.')}
		`;

		rp.host.bucket.openDialog({
			component: div,
			caption: msg('Two-Factor Authentication'),
			closable: true,
			width: 500,
			height: 250,
			resizable: true,
			location: 'center',
		});
	}

	async onAction(action: string) {
		switch (action) {
			case 'delete':
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `${msg('Are you sure you want to delete')} '${this.user.login_name}'?`
				});
				if (command?.id == 'Y') {
					await rp.ms('users').post('users', [Object.assign({_state: 'deleted'}, this.user)]);
					this.component.dispatchEvent(new CustomEvent('form.save', {bubbles: true, composed: true}));
				}
				break;
		}
	}
};
